import { useFirestore, useFirestoreDocData, useUser } from "reactfire";
import { doc, setDoc } from "firebase/firestore";
import { FIRESTORE_CARTS_TABLE } from "../../../constants/FirebaseConstants";
import {
  filterProduct,
  getPaymentAndDiscountPriceForGroup,
  isPreOrderProduct,
  isPreOrderProgramActive,
} from "../../../helpers/product";
import uuid from "uuid/v4";
import { db } from "../../../services/firebase";
import dayjs from "dayjs";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { isEqual } from "lodash";

export function useCart() {
  const { t } = useTranslation();
  const firestore = useFirestore();
  const { data: user, status: userStatus } = useUser();
  const docRef = doc(
    firestore,
    FIRESTORE_CARTS_TABLE,
    userStatus === "loading" || !user ? "0" : user?.uid,
  );
  const { data, status } = useFirestoreDocData(docRef, { idField: "id" });
  const isLoading = status === "loading" || !user;

  const addProductToCartLegacy = async ({
    product,
    quantity,
    notification,
  }) => {
    if (user) {
      const currentCart = !!data ? data.products : [];
      const cartItem = filterProduct(currentCart, product);

      const newCart =
        cartItem === undefined
          ? [
              ...currentCart,
              {
                ...product,
                quantity: quantity ? quantity : 1,
                cartItemId: uuid(),
              },
            ]
          : currentCart.map((item) =>
              item.cartItemId === cartItem.cartItemId
                ? {
                    ...item,
                    quantity: quantity
                      ? item.quantity + quantity
                      : item.quantity + 1,
                  }
                : item,
            );

      try {
        await setDoc(
          doc(db, FIRESTORE_CARTS_TABLE, user?.uid),
          { products: newCart, updatedAt: dayjs().unix() },
          { merge: true },
        );
        if (notification) {
          message.success(t("added_to_cart"));
        }
      } catch (err) {
        console.log(err);
      }
    }
  };

  // Centralized add with stock/pre-order split. Returns the actual split used.
  const addProductToCart = async ({ product, quantity, notification }) => {
    if (!user) return { normalQty: 0, preOrderQty: 0 };
    const currentCart = !!data ? data.products : [];

    const orderUnit = product.orderUnit || 1;
    const programActive = isPreOrderProgramActive(product.preOrder);

    // If caller explicitly targets a pre-order line, add directly to pre-order without consuming stock
    if (product.preOrderLine) {
      const requested = quantity || 1;
      let newCart = [...currentCart];
      const addLine = (lineProduct, qty) => {
        if (qty <= 0) return;
        const cartItem = filterProduct(newCart, lineProduct);
        if (cartItem === undefined) {
          newCart = [
            ...newCart,
            { ...lineProduct, quantity: qty, cartItemId: uuid() },
          ];
        } else {
          newCart = newCart.map((item) =>
            item.cartItemId === cartItem.cartItemId
              ? { ...item, quantity: item.quantity + qty }
              : item,
          );
        }
      };

      addLine({ ...product, preOrderLine: true }, requested);
      try {
        await setDoc(
          doc(db, FIRESTORE_CARTS_TABLE, user?.uid),
          { products: newCart, updatedAt: dayjs().unix() },
          { merge: true },
        );
        if (notification) message.success(t("added_to_cart"));
      } catch (err) {
        console.log(err);
      }
      return { normalQty: 0, preOrderQty: requested };
    }

    // Compute how many normal units are already in cart (exclude pre-order lines)
    const normalInCart = currentCart
      .filter((it) => it.id === product.id && !it.preOrderLine)
      .reduce((s, it) => s + (it.quantity || 0), 0);
    const stock = product.stock || 0;
    let inStockLeft = Math.max(0, stock - normalInCart);

    // Allocate from stock in multiples of orderUnit
    let allocatable = Math.floor(inStockLeft / orderUnit) * orderUnit;
    // Defensive: if no normal line exists and stock exists but allocatable 0, base on stock directly
    if (allocatable === 0 && stock >= orderUnit && normalInCart === 0) {
      allocatable = Math.floor(stock / orderUnit) * orderUnit;
    }

    const requested = quantity || 1;
    const normalQty = Math.min(requested, allocatable);
    const preOrderQty = Math.max(0, requested - normalQty);

    // Mutate a working copy in-memory, then write once
    let newCart = [...currentCart];

    const addLine = (lineProduct, qty) => {
      if (qty <= 0) return;
      const cartItem = filterProduct(newCart, lineProduct);
      if (cartItem === undefined) {
        newCart = [
          ...newCart,
          { ...lineProduct, quantity: qty, cartItemId: uuid() },
        ];
      } else {
        newCart = newCart.map((item) =>
          item.cartItemId === cartItem.cartItemId
            ? { ...item, quantity: item.quantity + qty }
            : item,
        );
      }
    };

    // Add normal portion (ensure preOrderLine not set)
    addLine({ ...product, preOrderLine: false }, normalQty);

    // Add pre-order portion if allowed (program active or product is in pre-order window)
    const preAllowed =
      preOrderQty > 0 &&
      (programActive ||
        (!!product.preOrder && product.preOrder.active) ||
        isPreOrderProduct(product.preOrder, product.stock, product.orderUnit));
    if (preAllowed) {
      addLine({ ...product, preOrderLine: true }, preOrderQty);
    }

    try {
      await setDoc(
        doc(db, FIRESTORE_CARTS_TABLE, user?.uid),
        { products: newCart, updatedAt: dayjs().unix() },
        { merge: true },
      );
      if (notification) message.success(t("added_to_cart"));
    } catch (err) {
      console.log(err);
    }

    return { normalQty, preOrderQty: preAllowed ? preOrderQty : 0 };
  };

  const addProductSetToCart = async ({ products }) => {
    const currentCart = data.products;
    let newCart = [...currentCart];
    products.forEach((product) => {
      const cartItem = filterProduct(currentCart, product);
      newCart =
        cartItem === undefined
          ? [
              ...newCart,
              {
                ...product,
                quantity: product.quantity ? product.quantity : 1,
                cartItemId: uuid(),
              },
            ]
          : newCart.map((item) =>
              item.cartItemId === cartItem.cartItemId
                ? {
                    ...item,
                    quantity: product.quantity
                      ? item.quantity + product.quantity
                      : item.quantity + 1,
                  }
                : item,
            );
    });

    try {
      await setDoc(
        doc(db, FIRESTORE_CARTS_TABLE, user?.uid),
        {
          products: newCart,
          updatedAt: dayjs().unix(),
        },
        { merge: true },
      );
    } catch (err) {
      console.log(err);
    }
  };

  const addMultipleProductsToCart = async ({ products }) => {
    const currentCart = data.products;
    let cartItemsList = [...currentCart];
    products.forEach((product) => {
      const cartItem = filterProduct(currentCart, product);
      if (cartItem === undefined) {
        cartItemsList.push({
          ...product,
          quantity: product.quantity ? product.quantity : 1,
          cartItemId: uuid(),
        });
      } else {
        let existingCartItemProduct = cartItemsList.find(
          (item) => item.cartItemId === cartItem.cartItemId,
        );
        let index = cartItemsList.findIndex(
          (item) => item.cartItemId === cartItem.cartItemId,
        );
        cartItemsList[index].quantity = product.quantity
          ? existingCartItemProduct.quantity + product.quantity
          : existingCartItemProduct.quantity + 1;
      }
    });

    try {
      await setDoc(
        doc(db, FIRESTORE_CARTS_TABLE, user?.uid),
        {
          products: cartItemsList,
          updatedAt: dayjs().unix(),
        },
        { merge: true },
      );
    } catch (err) {
      console.log(err);
    }
  };

  const decreaseProductQuantity = async ({ product }) => {
    const currentCart = data.products;
    const orderUnit = product.orderUnit || 1;
    if (product.quantity > orderUnit) {
      const newCart = currentCart.map((item) =>
        item.cartItemId === product.cartItemId
          ? { ...item, quantity: item.quantity - orderUnit }
          : item,
      );

      try {
        await setDoc(
          doc(db, FIRESTORE_CARTS_TABLE, user?.uid),
          {
            products: newCart,
            updatedAt: dayjs().unix(),
          },
          { merge: true },
        );
      } catch (err) {
        console.log(err);
      }
    }
  };

  const deleteProductFromCart = async ({ product }) => {
    const currentCart = data.products;
    const remainingItems = (currentCart, product) =>
      currentCart.filter(
        (cartItem) => cartItem.cartItemId !== product.cartItemId,
      );

    try {
      await setDoc(doc(db, FIRESTORE_CARTS_TABLE, user?.uid), {
        products: remainingItems(currentCart, product),
        updatedAt: dayjs().unix(),
      });
    } catch (err) {
      console.log(err);
    }
  };

  const deleteAllProductsFromCart = async () => {
    try {
      if (user?.uid) {
        await setDoc(doc(db, FIRESTORE_CARTS_TABLE, user?.uid), {
          products: [],
          updatedAt: dayjs().unix(),
        });
      }
    } catch (err) {
      console.log(err);
    }
  };

  const updateCart = async ({
    groupId,
    groupSettingsUserGroup,
    selectedSalesGroup,
    products,
  }) => {
    const currentCart = data?.products;
    if (
      !products ||
      products.length === 0 ||
      !currentCart ||
      currentCart.length === 0
    )
      return null;
    else {
      const newCart = currentCart.reduce(function (updatedCart, cartItem) {
        const productFound = products.find(
          (product) => product.id === cartItem.id,
        );
        const productPriceForUser = getPaymentAndDiscountPriceForGroup(
          productFound,
          groupId,
          groupSettingsUserGroup,
          selectedSalesGroup,
        );

        // Product found and stock is available or pre-order is active for product
        if (
          !!productFound &&
          ((productFound.stock > 0 &&
            productFound.stock >= cartItem.quantity) ||
            isPreOrderProduct(
              productFound.preOrder,
              productFound.stock,
              productFound.orderUnit,
            ) ||
            (cartItem.preOrderLine &&
              (isPreOrderProgramActive(productFound.preOrder) ||
                (!!productFound.preOrder && productFound.preOrder.active) ||
                isPreOrderProduct(
                  productFound.preOrder,
                  productFound.stock,
                  productFound.orderUnit,
                ))))
        ) {
          const updatedCartItem = {
            ...cartItem,
            name: productFound.name,
            image: productFound.image,
            price: productPriceForUser.original,
            discount: productFound.discount,
            taxRate: productFound.taxRate,
            stock: productFound.stock,
            preOrderLine: !!cartItem.preOrderLine,
          };
          updatedCart.push(updatedCartItem);
        }
        // Product found and stock is not enough -> quantity in cart should be equal to available stock
        else if (
          !!productFound &&
          productFound.stock > 0 &&
          productFound.stock < cartItem.quantity &&
          !cartItem.preOrderLine
        ) {
          const orderUnit = productFound.orderUnit || 1;
          const newStock =
            Math.trunc(productFound.stock / orderUnit) * orderUnit;
          const updatedCartItem = {
            ...cartItem,
            name: productFound.name,
            image: productFound.image,
            price: productPriceForUser.original,
            discount: productFound.discount,
            taxRate: productFound.taxRate,
            stock: productFound.stock,
            quantity: newStock,
            preOrderLine: !!cartItem.preOrderLine,
          };
          updatedCart.push(updatedCartItem);
        }
        return updatedCart;
      }, []);

      if (!isEqual(newCart, currentCart)) {
        try {
          await setDoc(
            doc(db, FIRESTORE_CARTS_TABLE, user?.uid),
            {
              products: newCart,
              updatedAt: dayjs().unix(),
            },
            { merge: true },
          );
        } catch (err) {
          console.log(err);
        }
      }
    }
  };

  return {
    data,
    isLoading,
    addProductToCartLegacy,
    addProductToCart,
    addProductSetToCart,
    addMultipleProductsToCart,
    decreaseProductQuantity,
    deleteProductFromCart,
    deleteAllProductsFromCart,
    updateCart,
  };
}
